import { useState, useEffect, useMemo, useRef } from 'react'
import Select, { components } from "react-select"
import RepAvatar from "../../Material/RepAvatar"
import { AllDriversQuery, RepsQuery, AllBoardsQuery, GroupsQuery } from "../Queries"
import { Query } from '@apollo/client/react/components'
import "./TypeAheadComponents.css"
import { useQuery, gql } from '@apollo/client'

export const GroupsTypeAhead = ({
    onChange,
    defaultValue,
    returnOptions = false,
    isMulti = true,
    placeholder = "Select Groups",
}) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [prevSelectedMap, setPrevSelectedMap] = useState({})

    useEffect(() => {
        const defaultItems = Array.isArray(defaultValue)
            ? defaultValue
            : defaultValue
                ? [defaultValue]
                : []

        if (defaultItems.length > 0) {
            const map = {}
            defaultItems.forEach(item => {
                if (typeof item === "string" || typeof item === "number") {
                    map[String(item)] = prevSelectedMap[item] || String(item)
                } else if (item?.value && item?.label) {
                    map[String(item.value)] = item.label
                }
            })
            setPrevSelectedMap(map)
        }
    }, [defaultValue])

    return (
        <Query query={GroupsQuery} variables={{ searchTerm }}>
            {({ loading, error, data }) => {
                if (loading) return <Select isLoading />
                if (error)
                    return (<Select isDisabledplaceholder={`Error! ${error.message}`} />)

                const options = data?.allGroups?.edges?.map((g) => ({
                    value: String(g.node.id),
                    label: g.node.name,
                })) || []

                const normalizeSelection = (selection) => {
                    if (isMulti) {
                        const selected = selection || []
                        return returnOptions ? selected : selected.map((groupOption) => groupOption.value)
                    }
                    return returnOptions ? (selection || null) : (selection ? selection.value : null)
                }

                if (defaultValue === undefined) {
                    return (
                        <Select
                            isMulti={isMulti}
                            isClearable={!isMulti}
                            placeholder={placeholder}
                            options={options}
                            defaultValue={undefined}
                            onInputChange={(val) => setSearchTerm(val)}
                            onChange={(sel) => onChange(normalizeSelection(sel))}
                        />
                    )
                }

                const normalizedDefaultValue = Array.isArray(defaultValue)
                    ? defaultValue
                    : defaultValue
                        ? [defaultValue]
                        : []
                const isIdArray = normalizedDefaultValue.length > 0 && (
                    typeof normalizedDefaultValue[0] === "string" || typeof normalizedDefaultValue[0] === "number"
                )

                let controlledValue = null

                if (isIdArray) {
                    const normalizedIds = normalizedDefaultValue.map(String)
                    const selectedInOptions = options.filter(opt => normalizedIds.includes(opt.value))
                    const missingSelected = normalizedIds
                        .filter(id => !selectedInOptions.some(opt => opt.value === id))
                        .map(id => ({
                            value: id,
                            label: options.find(o => o.value === id)?.label || prevSelectedMap[id] || id,
                        }))
                    controlledValue = [...selectedInOptions, ...missingSelected]
                } else {
                    controlledValue = normalizedDefaultValue.map(item => ({ value: String(item.value), label: item.label }))
                }
                return (
                    <Select
                        isMulti={isMulti}
                        isClearable={!isMulti}
                        placeholder={placeholder}
                        options={options}
                        value={isMulti ? controlledValue : (controlledValue[0] || null)}
                        onInputChange={(val) => setSearchTerm(val)}
                        onChange={(sel) => onChange(normalizeSelection(sel))}
                    />
                )
            }}
        </Query>
    )
}


export const BoardTypeAhead = ({ onChange, defaultValue, isDisabled = false }) => {
    const [searchTerm, setSearchTerm] = useState("")

    const { loading, error, data } = useQuery(AllBoardsQuery, {
        variables: { searchTerm },
    })

    if (error) return <Select isDisabled placeholder={`Error! ${error.message}`} />

    const options = data?.allBoards?.edges?.map(board => ({
        value: board.node.id,
        label: board.node.title,
    })) || []

    const defaultOption = defaultValue ? options.find(option => option.value === defaultValue) : null

    return (
        <Select
            options={options}
            isLoading={loading}
            isDisabled={isDisabled}
            placeholder="Select Board"
            onInputChange={(inputValue) => setSearchTerm(inputValue)}
            onChange={(selected) => onChange(selected.value)}
            defaultValue={defaultOption}
            key="boardTypeAhead" // defaultValue won't work without this https://github.com/JedWatson/react-select/issues/5888
        />
    )
}

const DriverByIdQuery = gql`query DriverById($id: ID!){
    driver(id: $id){ id pk name branch{ id name } }
}`

export const DriverTypeAhead = ({ onChange, defaultValue, defaultLabelValue, isDisabled }) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [selectedDriver, setSelectedDriver] = useState(null)

    const { loading, error, data } = useQuery(AllDriversQuery, {
        variables: searchTerm ? { searchTerm } : {}
    })

    const { data: singleDriverData } = useQuery(DriverByIdQuery, {
        variables: { id: defaultValue },
        skip: !defaultValue || !!selectedDriver,
        fetchPolicy: 'cache-first',
    })

    const options = useMemo(() => {
        return (
            data?.allDrivers?.edges?.map(driver => ({
                value: driver.node.id,
                label: `#${driver.node.pk} · ${driver.node.name} · ${driver.node.branch.name ?? ""}`
            })) || []
        )
    }, [data])

    // Resolve default selection from options list first
    useEffect(() => {
        if (selectedDriver) return

        if (defaultLabelValue) {
            setSelectedDriver(defaultLabelValue)
        } else if (defaultValue) {
            const match = options.find(o => o.value === defaultValue)
            if (match) setSelectedDriver(match)
        }
    }, [options, defaultValue, defaultLabelValue, selectedDriver])

    // Fall back to singular driver query when not found in initial options
    useEffect(() => {
        if (selectedDriver || !defaultValue || !singleDriverData?.driver) return
        const d = singleDriverData.driver
        setSelectedDriver({
            value: d.id,
            label: `#${d.pk} · ${d.name} · ${d.branch?.name ?? ""}`
        })
    }, [singleDriverData, defaultValue, selectedDriver])

    if (loading) return <Select isLoading />
    if (error)
        return (
            <Select
                isDisabled
                placeholder={`Error! ${error.message}`}
            />
        )

    return (
        <Select
            options={options}
            placeholder="Start typing to search for drivers..."
            value={selectedDriver}
            onInputChange={(inputValue, { action }) => {
                if (action === "input-change") {
                    setSearchTerm(inputValue)
                }
            }}
            onChange={(selected) => {
                setSelectedDriver(selected)
                onChange(selected.value)
            }}
        />
    )
}

const RepOption = (props) => (
    <components.Option {...props}>
        <div className="d-flex align-items-center">
            <RepAvatar rep={props.data.node} keepClose />
            <span className="ml-2">{props.label}</span>
        </div>
    </components.Option>
)

export const RepsTypeAhead = ({ onChange, defaultValue, group, permissions, returnObjects = false, boardId = null, extraRepOptions = [] }) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [selectedReps, setSelectedReps] = useState(
        Array.isArray(defaultValue) && defaultValue.every((item) => item && typeof item === "object")
            ? defaultValue
            : []
    )
    const lastSyncedDefaultSignature = useRef(null)
    const hasDefaultValue = Array.isArray(defaultValue)
    const isArrayOfStrings = hasDefaultValue && defaultValue.every(id => typeof id === "string")
    const defaultSignature = useMemo(() => {
        if (!hasDefaultValue) {
            return null
        }
        return defaultValue
            .map((item) => {
                if (typeof item === "string") return item
                return item?.id || item?.value || ""
            })
            .join("|")
    }, [defaultValue, hasDefaultValue])

    const handleSelectChange = (node) => {
        if (!node) return
        if (!selectedReps.some(r => r.id === node.id)) {
            const updated = [...selectedReps, node]
            setSelectedReps(updated)
            onChange(returnObjects ? updated : updated.map(r => r.id))
        }
    }

    const handleRemoveRep = (repId) => {
        setSelectedReps(prevSelected => {
            const updated = prevSelected.filter(r => r.id !== repId)
            onChange(returnObjects ? updated : updated.length > 0 ? updated.map(r => r.id) : [])
            return updated
        })
    }

    const handleSearch = (inputValue) => {
        setSearchTerm(inputValue && inputValue.trim().length > 0 ? inputValue.trim() : "")
    }

    const queryVariables = useMemo(() => ({
        searchTerm,
        groups: group,
        permissions,
        boardId,
    }), [searchTerm, group, permissions, boardId])

    const { data, loading, error } = useQuery(RepsQuery, {
        variables: queryVariables,
        fetchPolicy: "cache-first",
        returnPartialData: true,
        notifyOnNetworkStatusChange: false,
    })

    const availableRepOptions = useMemo(() => {
        let allReps = data?.allReps?.edges?.map((rep) => ({
            value: rep.node.id,
            label: (rep.node.name || rep.node.username).trim(),
            node: rep.node,
        })) || []

        const normalizedExtraOptions = (extraRepOptions || [])
            .filter((rep) => rep?.id)
            .map((rep) => ({
                value: rep.id,
                label: rep.name || rep.username || `${rep.firstName || ""} ${rep.lastName || ""}`.trim(),
                node: rep,
            }))

        const seenValues = new Set(allReps.map((rep) => rep.value))
        normalizedExtraOptions.forEach((rep) => {
            if (!seenValues.has(rep.value)) {
                allReps.push(rep)
                seenValues.add(rep.value)
            }
        })

        return allReps
    }, [data, extraRepOptions])

    useEffect(() => {
        if (!hasDefaultValue) {
            return
        }

        if (defaultValue.length === 0) {
            setSelectedReps([])
            lastSyncedDefaultSignature.current = defaultSignature
            return
        }

        if (!isArrayOfStrings && lastSyncedDefaultSignature.current !== defaultSignature) {
            setSelectedReps(defaultValue)
            lastSyncedDefaultSignature.current = defaultSignature
        }
    }, [defaultValue, defaultSignature, hasDefaultValue, isArrayOfStrings])

    useEffect(() => {
        if (!hasDefaultValue) return
        if (!isArrayOfStrings) return

        if (lastSyncedDefaultSignature.current === defaultSignature) {
            return
        }

        if (availableRepOptions.length === 0 || defaultValue.length === 0) {
            if (defaultValue.length === 0) {
                setSelectedReps([])
                lastSyncedDefaultSignature.current = defaultSignature
            }
            return
        }

        const repObjects = defaultValue
            .map((id) => availableRepOptions.find((rep) => rep.value === id)?.node)
            .filter(Boolean)

        if (repObjects.length !== defaultValue.length) {
            return
        }
        setSelectedReps((prevSelected) => {
            const nextSignature = repObjects.map((rep) => rep.id).join("|")
            const prevSignature = prevSelected.map((rep) => rep.id).join("|")
            return prevSignature === nextSignature ? prevSelected : repObjects
        })
        lastSyncedDefaultSignature.current = defaultSignature
    }, [defaultValue, defaultSignature, availableRepOptions, hasDefaultValue, isArrayOfStrings])

    const selectedIds = selectedReps.map((rep) => rep.id || rep.value).filter(Boolean)
    const dropdownOptions = availableRepOptions.filter((rep) => !selectedIds.includes(rep.value))

    return (
        <div className="reps-typeahead-container">
            {selectedReps.length > 0 && (
                <div className="selected-reps">
                    {selectedReps.filter((rep) => rep?.id).map((rep) => (
                        <div key={rep.id || rep.pk} className="selected-rep-chip">
                            <RepAvatar rep={rep} keepOpen />
                            <i className="fa fa-times remove-rep" onClick={() => handleRemoveRep(rep.id)} />
                        </div>
                    ))}
                </div>
            )}

            {error ? (
                <div className="text-danger text-center py-2">Error loading reps: {error.message}</div>
            ) : (
                <Select
                    options={dropdownOptions}
                    isLoading={loading}
                    components={{ Option: RepOption }}
                    placeholder="Search and select reps..."
                    onInputChange={handleSearch}
                    onChange={(selected) => handleSelectChange(selected?.node)}
                    isClearable={false}
                    value={null}
                    blurInputOnSelect={true}
                    noOptionsMessage={() => searchTerm ? "No reps found for this search." : "No reps available."}
                />
            )}
        </div>
    )
}

export { default as RepTypeAhead } from './RepTypeAhead'
